'use client';

import { analyticsLocation, ensureGoogleAnalytics } from '@/lib/google-analytics';

/**
 * GA4Service — Google Analytics 4 event tracking singleton.
 *
 * Mirrors existing Faro/RUM events into GA4 via the global gtag function.
 * All methods are fire-and-forget: they never throw, never await, and never
 * affect the Faro pipeline or user experience.
 *
 * GA4 naming rules enforced here:
 * - Event names: lowercase, underscores, max 40 chars
 * - Parameter values: strings max 100 chars, up to 25 custom parameters per event
 */

// Window.gtag type is declared in src/types/gtag.d.ts

function isAvailable(): boolean {
  return ensureGoogleAnalytics();
}

/** Truncate string values to GA4's 100-char limit. */
function truncate(value: unknown): string | undefined {
  if (value == null) return undefined;
  const str = String(value);
  return str.length > 100 ? str.slice(0, 100) : str;
}

class GA4Service {
  private lastPageLocation = '';
  /** Send a GA4 page_view event. */
  trackPageView(path: string, title?: string): void {
    try {
      if (!isAvailable()) return;
      const target = new URL(path, window.location.origin);
      // RUM initialization/popstate supplies a pathname; the router supplies the full URL.
      // Normalize both to the current campaign URL so they describe the same page view.
      if (!target.search && target.pathname === window.location.pathname) target.search = window.location.search;
      const location = analyticsLocation(target.toString());
      if (location === this.lastPageLocation) return;
      const referrer = this.lastPageLocation || (document.referrer ? analyticsLocation(document.referrer) : '');
      window.gtag!('set', { page_location: location, page_referrer: referrer });
      window.gtag!('event', 'page_view', {
        page_path: new URL(location).pathname,
        page_location: location,
        page_referrer: referrer,
        page_title: truncate(title),
      });
      this.lastPageLocation = location;
    } catch {
      // fire-and-forget — never propagate
    }
  }

  /** Send GA4 game lifecycle events (room_create, room_join, etc.). */
  trackGameLifecycle(event: {
    action: string;
    gameType: string;
    participantCount?: number;
    duration?: number;
  }): void {
    try {
      if (!isAvailable()) return;
      window.gtag!('event', event.action, {
        game_type: truncate(event.gameType),
        participant_count: event.participantCount,
        duration: event.duration,
      });
    } catch {
      // fire-and-forget
    }
  }

  /** Send a GA4 conversion_step event. */
  trackConversionStep(step: {
    funnel: string;
    step: string;
    success: boolean;
    duration?: number;
    errorReason?: string;
  }): void {
    try {
      if (!isAvailable()) return;
      window.gtag!('event', 'conversion_step', {
        funnel: truncate(step.funnel),
        step: truncate(step.step),
        success: step.success,
        duration: step.duration,
        error_reason: truncate(step.errorReason),
      });
    } catch {
      // fire-and-forget
    }
  }

  /**
   * Send a GA4 townhall_audience_entry event when a viewer actually enters
   * (after the in-app cover's tap, never on a preload). `entry_surface` and
   * `src` let X traffic be read apart from link-preview preload inflation.
   */
  trackAudienceEntry(entry: { roomId: string; entrySurface?: string; src?: string | null }): void {
    try {
      if (!isAvailable()) return;
      window.gtag!('event', 'townhall_audience_entry', {
        room_id: truncate(entry.roomId),
        entry_surface: truncate(entry.entrySurface ?? 'browser'),
        src: truncate(entry.src ?? undefined),
      });
    } catch {
      // fire-and-forget
    }
  }

  /** Send a GA4 feature_usage event. Skips page_engagement to avoid inflating GA4 quotas. */
  trackFeatureUsage(featureName: string, action: string, metadata?: Record<string, any>): void {
    try {
      if (!isAvailable()) return;
      // page_engagement fires on every visibility change + 30s inactivity checks —
      // high-frequency events that would dominate GA4 event stream and consume quotas.
      // These are already tracked in Faro/Grafana where volume is not an issue.
      if (featureName === 'page_engagement') return;
      window.gtag!('event', 'feature_usage', {
        feature_name: truncate(featureName),
        action: truncate(action),
        ...(metadata?.path && { page_path: truncate(metadata.path) }),
      });
    } catch {
      // fire-and-forget
    }
  }

  /** Send a GA4 error_recovery event. */
  trackErrorRecovery(errorType: string, recoveryAction: string, success: boolean): void {
    try {
      if (!isAvailable()) return;
      window.gtag!('event', 'error_recovery', {
        error_type: truncate(errorType),
        recovery_action: truncate(recoveryAction),
        success,
      });
    } catch {
      // fire-and-forget
    }
  }

  /** Set GA4 user properties for cross-session analysis. */
  setUserProperties(userId: string): void {
    try {
      if (!isAvailable()) return;
      window.gtag!('set', 'user_properties', {
        custom_user_id: truncate(userId),
      });
    } catch {
      // fire-and-forget
    }
  }
}

/** Singleton instance. */
export const ga4Service = new GA4Service();
